import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, Shield } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { z } from 'zod'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

const setupSchema = z.object({
  password: z.string().min(1, 'Password is required'),
})

type SetupFormData = z.infer<typeof setupSchema>

export function TwoFactorSetupForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [userEmail, setUserEmail] = useState('')

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { enable2FASetup } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()
  const colors = useThemeColors()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetupFormData>({
    resolver: zodResolver(setupSchema),
  })

  useEffect(() => {
    const email = searchParams.get('email')
    if (email) {
      setUserEmail(email)
    } else {
      const timer = setTimeout(() => {
        const emailCheck = new URLSearchParams(window.location.search).get('email')
        if (!emailCheck) {
          navigate('/login')
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [searchParams, navigate])

  const onSubmit = async (data: SetupFormData) => {
    if (!userEmail) {
      setError('Email is required')
      return
    }

    try {
      setIsLoading(true)
      setError('')

      const result = await enable2FASetup(userEmail, data.password)

      if (result.success && result.twoFactorRequired) {
        navigate(`/two-factor?email=${encodeURIComponent(userEmail)}`)
      } else {
        setError(result.message || 'Failed to enable 2FA. Please try again.')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to enable 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  if (!userEmail) {
    return null
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'} alt={siteSettings.site_name || 'Logo'} style={{ maxWidth: '300px', height: 'auto' }} />
              </Box>

              <Box mb={3} display="flex" justifyContent="center">
                <Shield size={48} color="#3182CE" />
              </Box>

              <Heading size="lg" color={colors.heading} mb={2}>
                Two-Factor Authentication Required
              </Heading>

              <Text color={colors.text} fontSize="sm" mb={1}>
                Your administrator has made two-factor authentication mandatory for all users.
              </Text>
              <Text color={colors.text} fontSize="sm" mb={2}>
                Please enable 2FA to continue logging in.
              </Text>
              <Text color={colors.email} fontWeight="medium" fontSize="sm">
                {userEmail}
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                You&apos;ll need to enter your password again to enable 2FA. After enabling, you&apos;ll receive a verification code via email.
              </Text>
            </Alert>

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={3} w="100%">
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input {...register('password')} type={showPassword ? 'text' : 'password'} placeholder="Enter your password" pl="10" />
                    <InputRightElement width="3rem">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        p={0}
                        type="button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        h="1.75rem"
                        minW="1.75rem"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <Button type="submit" bg="blue.500" color="white" _hover={{ bg: 'blue.600' }} size="lg" w="full" isLoading={isLoading} loadingText="Enabling 2FA...">
                  Enable Two-Factor Authentication
                </Button>
              </VStack>
            </Box>

            <Button variant="ghost" color={colors.link} fontSize="sm" onClick={handleBackToLogin}>
              Back to Login
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

